import { Dialog, DialogContent, LinearProgress } from '@mui/material';
import useSWR from 'swr';

import type { ContractFormSection } from '~/components/contracts/form/ContractFormDialog';
import { ContractFormDialog } from '~/components/contracts/form/ContractFormDialog';
import DialogTitleWithCloseButton from '~/components/DialogTitleWithCloseButton';
import { contractsRetrieve } from '~/data/generated/endpoints/contracts/contracts';
import { propertiesContractsRetrieve } from '~/data/generated/endpoints/properties/properties';
import { parseApiResponse } from '~/data/parseApiResponse';
import { contractSchema, propertyContractSchema } from '~/data/schemas/contract';

type Props = {
  contractId: string;
  onClose: () => void;
  /** Which section of the form to open on. Defaults to General. */
  initialSection?: ContractFormSection;
};

/**
 * Opens the existing Contract edit form for a contract chosen from the global
 * contracts list. A Contract can span multiple properties; this wrapper edits
 * it in the context of its first property (the form is per-property scoped).
 */
export const ContractEditDialog = ({ contractId, onClose, initialSection }: Props) => {
  const {
    data: contract,
    isLoading: isContractLoading,
    error: contractError,
  } = useSWR(['contract', contractId] as const, async ([, id]) => {
    const { data } = await contractsRetrieve(id);
    return parseApiResponse(contractSchema, data, 'Contract');
  });

  // The Contract has many PropertyContracts; the edit form is scoped to one
  // property. Default to the first property the contract is attached to.
  const firstPropertyContractMeta = contract?.propertyContracts?.[0] ?? null;
  const propertyId = firstPropertyContractMeta?.propertyId ?? null;
  const propertyContractId = firstPropertyContractMeta?.id ?? null;

  const {
    data: propertyContract,
    isLoading: isPropertyContractLoading,
    error: propertyContractError,
  } = useSWR(
    propertyId && propertyContractId
      ? (['property-contract', propertyId, propertyContractId] as const)
      : null,
    async ([, pid, pcid]) => {
      // The nested URL is /properties/{propertyId}/contracts/{pk}/ where pk is
      // the PropertyContract id, not the master Contract id.
      const { data } = await propertiesContractsRetrieve(pid, pcid);
      return parseApiResponse(propertyContractSchema, data, 'Property Contract');
    },
  );

  const isLoading = isContractLoading || isPropertyContractLoading;

  if (contractError || propertyContractError) {
    return (
      <Dialog open onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitleWithCloseButton onClose={onClose} title="Edit Contract Summary" />
        <DialogContent>Failed to load contract. Please try again.</DialogContent>
      </Dialog>
    );
  }

  if (isLoading || !contract) {
    return (
      <Dialog open onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitleWithCloseButton onClose={onClose} title="Edit Contract Summary" />
        <DialogContent>
          <LinearProgress />
        </DialogContent>
      </Dialog>
    );
  }

  if (!propertyId || !propertyContractId || !propertyContract) {
    // Defensive: a contract with no property contracts shouldn't reach the
    // edit affordance; surface a closable empty dialog instead of crashing.
    return (
      <Dialog open onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitleWithCloseButton onClose={onClose} title="Edit Contract Summary" />
        <DialogContent>This contract has no associated property to edit.</DialogContent>
      </Dialog>
    );
  }

  const { attachments, ...contractFields } = contract;

  return (
    <ContractFormDialog
      open
      onClose={onClose}
      type="edit"
      propertyId={propertyId}
      propertyContractId={propertyContractId}
      contractId={contract.id}
      initialSection={initialSection}
      defaultValues={{
        action: 'update',
        ...contractFields,
        serviceProvider: contract.serviceProvider?.id ?? '',
        // The read schema allows null for these fields (null=True columns); the
        // form fields are non-nullable, so hydrate a missing value as its
        // empty-input default.
        startDate: contract.startDate ?? '',
        expirationDate: contract.expirationDate ?? '',
        businessHours: contract.businessHours ?? '',
        cancellation: contract.cancellation ?? '',
        partsAndMaterialsMarkup: contract.partsAndMaterialsMarkup ?? 0,
        hourlyFeeAdjustmentRate: contract.hourlyFeeAdjustmentRate ?? 0,
        notes: contract.notes ?? '',
        propertyContract: {
          ...propertyContract,
          // The read schema allows a null monthly_price; the form field is a
          // plain string, so hydrate a missing price as an empty input.
          monthlyPrice: propertyContract.monthlyPrice ?? '',
          // elevatorType is a read-only display field the read now allows null
          // (Car.elevator_type is null=True); the form car omits it (optional),
          // so coerce a null to undefined.
          cars: propertyContract.cars.map((car) => ({
            ...car,
            elevatorType: car.elevatorType ?? undefined,
          })),
        },
        customDetails: contract.customDetails ?? [],
      }}
    />
  );
};
